import { useMemo, useSyncExternalStore } from 'react';

// How an overlay panel clears the device safe areas.
//
// A panel that touches a screen edge has to keep its content out of the safe
// area on that edge: the notch, the rounded corner, the home indicator. The
// insets are 0 unless the app sets `viewport-fit=cover` in its viewport meta
// tag, so an app that does not opt in sees no change.
//
// `Sheet`, `Drawer` and the mobile `Sidebar` all work the same way.
//
// `SAFE_AREA_PADDING` is the padding. Its four utilities carry no variant, so
// a caller's padding class replaces them through `cn()`: `p-0` zeroes all four
// edges, `p-6` sets 24px on all four, `px-6` and `py-6` take one axis each, and
// `pl-6` / `pr-6` / `pt-6` / `pb-6` take one edge each. Each reads a custom
// property.
//
// The one override that does not replace it is a logical one. tailwind-merge
// cannot tell whether `ps-6` means left or right, so it does not treat it as
// conflicting with the `pl-`/`pr-` here, and both survive the merge. Tailwind
// then emits `ps-6` before the panel's arbitrary-value `pl-`/`pr-`, so the
// panel wins and the caller's class does nothing. Pass `pl-*` / `pr-*`,
// `px-*`, or `--panel-padding` instead.
//
// Do not close that gap by making these four utilities logical. tailwind-merge
// then fails to resolve `px-*`, `pl-*` and `pr-*` against `ps-`/`pe-` as well,
// which trades one uncommon leak for three common ones. The physical form is
// the smaller of the two gaps, and it is deliberate.
//
// A panel sets `--panel-padding` to the padding it wants on an edge that faces
// into the page, and one `--panel-inset-*` property per edge that touches the
// screen. `SAFE_AREA_INSET` holds those properties, each already the sum of
// `--panel-padding` and the device inset, so a panel picks its edges and says
// nothing about the arithmetic. An edge a panel leaves unset falls back to
// `--panel-padding`, which itself defaults to 0.
//
// A caller that wants different padding and still wants the insets passes
// `[--panel-padding:1.5rem]` rather than `p-6`.
//
// `Sheet` and `Sidebar` know which edges they touch, so they pick from
// `SAFE_AREA_INSET` in JavaScript. `Drawer` does not: vaul puts the direction
// on the panel as `data-vaul-drawer-direction` rather than passing it to
// `DrawerContent`, so `SAFE_AREA_INSET_BY_DRAWER_DIRECTION` sets the same
// properties from CSS. The padding utilities stay variant-free either way,
// which is what keeps a caller's override working.
//
// Internal: not part of the package's public exports.

type SafeAreaEdge = 'top' | 'right' | 'bottom' | 'left';

const SAFE_AREA_PADDING =
  'pt-[var(--panel-inset-top,var(--panel-padding,0px))] pr-[var(--panel-inset-right,var(--panel-padding,0px))] pb-[var(--panel-inset-bottom,var(--panel-padding,0px))] pl-[var(--panel-inset-left,var(--panel-padding,0px))]';

const SAFE_AREA_INSET: Record<SafeAreaEdge, string> = {
  top: '[--panel-inset-top:calc(var(--panel-padding,0px)+env(safe-area-inset-top,0px))]',
  right:
    '[--panel-inset-right:calc(var(--panel-padding,0px)+env(safe-area-inset-right,0px))]',
  bottom:
    '[--panel-inset-bottom:calc(var(--panel-padding,0px)+env(safe-area-inset-bottom,0px))]',
  left: '[--panel-inset-left:calc(var(--panel-padding,0px)+env(safe-area-inset-left,0px))]',
};

// The same properties, keyed off vaul's direction attribute. Each direction
// names the edges its panel touches.
//
// The `calc()` is spelled out rather than interpolated from `SAFE_AREA_INSET`.
// Tailwind only generates a class it finds as literal text in the source, so
// a template literal would build names that never reach the stylesheet and the
// drawer would lose its insets with nothing to fail on.
const SAFE_AREA_INSET_BY_DRAWER_DIRECTION = [
  'data-[vaul-drawer-direction=top]:[--panel-inset-top:calc(var(--panel-padding,0px)+env(safe-area-inset-top,0px))] data-[vaul-drawer-direction=top]:[--panel-inset-left:calc(var(--panel-padding,0px)+env(safe-area-inset-left,0px))] data-[vaul-drawer-direction=top]:[--panel-inset-right:calc(var(--panel-padding,0px)+env(safe-area-inset-right,0px))]',
  'data-[vaul-drawer-direction=bottom]:[--panel-inset-bottom:calc(var(--panel-padding,0px)+env(safe-area-inset-bottom,0px))] data-[vaul-drawer-direction=bottom]:[--panel-inset-left:calc(var(--panel-padding,0px)+env(safe-area-inset-left,0px))] data-[vaul-drawer-direction=bottom]:[--panel-inset-right:calc(var(--panel-padding,0px)+env(safe-area-inset-right,0px))]',
  'data-[vaul-drawer-direction=right]:[--panel-inset-top:calc(var(--panel-padding,0px)+env(safe-area-inset-top,0px))] data-[vaul-drawer-direction=right]:[--panel-inset-bottom:calc(var(--panel-padding,0px)+env(safe-area-inset-bottom,0px))] data-[vaul-drawer-direction=right]:[--panel-inset-right:calc(var(--panel-padding,0px)+env(safe-area-inset-right,0px))]',
  'data-[vaul-drawer-direction=left]:[--panel-inset-top:calc(var(--panel-padding,0px)+env(safe-area-inset-top,0px))] data-[vaul-drawer-direction=left]:[--panel-inset-bottom:calc(var(--panel-padding,0px)+env(safe-area-inset-bottom,0px))] data-[vaul-drawer-direction=left]:[--panel-inset-left:calc(var(--panel-padding,0px)+env(safe-area-inset-left,0px))]',
].join(' ');

// Popups positioned by Radix (Popover, DropdownMenu, Select, Tooltip,
// HoverCard) are placed in JavaScript, not by these classes. Radix keeps a
// popup inside the viewport with `collisionPadding`, which is a number of
// pixels and cannot read `env()`. With it at Radix's default, a menu that would
// overflow the right edge is shifted flush against the raw edge, under a
// landscape cutout. `useSafeAreaCollisionPadding` reads the insets in pixels
// and adds them to the component's own margin, so the popup is kept clear of
// the cutouts the same way the padded page content is.
//
// The insets are read from one hidden fixed element whose padding is the four
// `env()` values: `getComputedStyle` resolves `env()` in a padding, not in a
// custom property. They are re-read when a popup mounts, and on resize and
// orientation change (again one frame later), the only times they move. The
// server and the hydration pass see zeros, which is what an app without
// `viewport-fit=cover` gets anyway, and popups are closed on first render.

type Insets = { top: number; right: number; bottom: number; left: number };

const NO_INSETS: Insets = { top: 0, right: 0, bottom: 0, left: 0 };

let insets = NO_INSETS;
let probe: HTMLElement | null = null;

function readInsets() {
  if (!probe) {
    probe = document.createElement('div');
    probe.setAttribute('aria-hidden', 'true');
    probe.setAttribute('data-safe-area-probe', '');
    probe.style.cssText =
      'position:fixed;top:0;left:0;width:0;height:0;visibility:hidden;pointer-events:none;padding:env(safe-area-inset-top,0px) env(safe-area-inset-right,0px) env(safe-area-inset-bottom,0px) env(safe-area-inset-left,0px)';
    document.body.appendChild(probe);
  }
  const style = getComputedStyle(probe);
  const next: Insets = {
    top: parseFloat(style.paddingTop) || 0,
    right: parseFloat(style.paddingRight) || 0,
    bottom: parseFloat(style.paddingBottom) || 0,
    left: parseFloat(style.paddingLeft) || 0,
  };
  // Keep the same object while nothing moved: useSyncExternalStore re-renders
  // on every new snapshot identity.
  if (
    next.top !== insets.top ||
    next.right !== insets.right ||
    next.bottom !== insets.bottom ||
    next.left !== insets.left
  ) {
    insets = next;
  }
  return insets;
}

// Every mounted popup shares the one cache, so one window listener reads it
// and notifies them all. A listener per popup would not do: the first to see
// a change updates the cache, and the rest then compare against the new value
// and miss it. A menu and its open submenu are two popups at once.
const listeners = new Set<() => void>();
let frame = 0;

function refresh() {
  const before = insets;
  if (readInsets() !== before) listeners.forEach((listener) => listener());
}

// Read again a frame later too. A 180 degree landscape turn moves the cutout
// to the other side without changing the viewport size, and the browser may
// update `env()` after it fires `orientationchange`.
function onViewportChange() {
  refresh();
  cancelAnimationFrame(frame);
  frame = requestAnimationFrame(refresh);
}

function subscribe(onChange: () => void) {
  if (listeners.size === 0) {
    window.addEventListener('resize', onViewportChange);
    window.addEventListener('orientationchange', onViewportChange);
  }
  listeners.add(onChange);
  // Nothing refreshes the cache while no popup is mounted, so a rotation in
  // that time leaves it stale. Re-read now and tell every popup if it moved.
  refresh();
  return () => {
    listeners.delete(onChange);
    if (listeners.size === 0) {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', onViewportChange);
      window.removeEventListener('orientationchange', onViewportChange);
    }
  };
}

const getSnapshot = () => (probe ? insets : readInsets());
const getServerSnapshot = () => NO_INSETS;

/**
 * The `collisionPadding` a Radix popup needs to stay clear of the device safe
 * areas: `base` pixels on every edge, plus that edge's inset.
 */
function useSafeAreaCollisionPadding(base = 0): Insets {
  const current = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getServerSnapshot,
  );
  return useMemo(
    () => ({
      top: base + current.top,
      right: base + current.right,
      bottom: base + current.bottom,
      left: base + current.left,
    }),
    [base, current],
  );
}

export {
  SAFE_AREA_INSET,
  SAFE_AREA_INSET_BY_DRAWER_DIRECTION,
  SAFE_AREA_PADDING,
  useSafeAreaCollisionPadding,
};
